import React from 'react'
import ReactDOMClient from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import thunk from 'redux-thunk'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { createBrowserHistory } from 'history'
import { ConnectedRouter, push, routerMiddleware } from 'connected-react-router'
import { Plugins } from '@leverege/plugin'
import { DynamicStore } from '@leverege/ui-plugin'
import Api from '@leverege/api'
import { PluginSetup as RuleSetup } from '@leverege/rule'
import { PluginSetup as UIRuleSetup } from '@leverege/ui-rule'
import { PluginSetup as ConditionSetup } from '@leverege/condition'
import { PluginSetup as UIConditionSetup } from '@leverege/ui-condition'
import { PluginSetup as UIAttributeSetup } from '@leverege/ui-attributes'
import { PluginSetup as StylePluginSetup } from '@leverege/ui-style-elements'
import { ImagineFilterPluginSetup, ImagineSortPluginSetup } from '@leverege/molten/imagine'
import { GlobalState } from '@leverege/ui-redux'
import { ApiProvider, Auth } from '@leverege/api-redux'
import ActiveTheme from '@leverege/ui-active-theme'
import { Theme, Dialogs, ErrorBoundary } from '@leverege/ui-elements'
import I18N from '@leverege/i18n'

import AppTheme from './theme'
import App from './App'
import Deprecated from './ApiDeprecated'

import reducers from './reducers'
import PluginSetup from './PluginSetup'

import '~/style/global-style.less' // eslint-disable-line 

async function start( options = { } ) {

  navigator.serviceWorker.getRegistrations().then( ( registrations ) => {
    // eslint-disable-next-line no-restricted-syntax
    for ( const registration of registrations ) {
      registration.unregister()
    }
  } )
  
  PluginSetup.install( Plugins )
  await I18N.init( {} )
  RuleSetup.install( Plugins )
  UIRuleSetup.install( Plugins )
  ConditionSetup.install( Plugins )
  UIConditionSetup.install( Plugins )
  UIAttributeSetup.install( Plugins )
  ImagineFilterPluginSetup.install( Plugins )
  ImagineSortPluginSetup.install( Plugins )
  StylePluginSetup.install( Plugins )

  // This is letting me know if the new page load was from a 'back' button
  // or a forward link. This is used to control the animation direction.
  // Unfortunately the 'forward' does not work. I think that embedding some
  // sequential id in the history state object is the correct way to do this,
  // but will have to modify/use different/write a router. This is currently
  // using a hacked up react-router v4
  function setupHistory( history ) {
    const pushState = history.pushState
    const forward = history.forward
    let ignore = false

    window.onpopstate = function( event ) {
      // console.log( "POP STATE", event )
      if ( ignore == false ) { // eslint-disable-line eqeqeq
        window.lastLocationWasBack = true
      } else {
        ignore = true
      }
    }

    history.pushState = function( state ) { // eslint-disable-line no-param-reassign
      window.lastLocationWasBack = false
      if ( typeof history.onpushstate === 'function' ) {
        history.onpushstate( { state } )
      }
      // ... whatever else you want to do
      // maybe call onhashchange e.handler
      return pushState.apply( history, arguments )
    }
    history.forward = () => { // eslint-disable-line no-param-reassign
      ignore = true
      forward()
    }
  }

  const history = createBrowserHistory( { basename : '/' } )

  // setupHistory( window.history )

  // window.onpushstate = ( event ) => {
  //   window.lastLocationWasBack = false
  // }

  // Needed for onClick 
  // http://stackoverflow.com/a/34015469/988941 
  // injectTapEventPlugin();
  const host = options?.api?.host || process.env.IMAGINE_HOST_URL
  const api = Api.init( { host, storeToken : true } )
  api.api = api // hack..the thunks need to be corrected to use { api }
  Deprecated.addDeprecated( api )

  const dynamicStore = new DynamicStore( { 
    reducers : reducers( { history } ), 
    middleware : [ thunk.withExtraArgument( api ), routerMiddleware( history ) ] } // Odd. should pass { }
  )
  const store = dynamicStore.create()
  GlobalState.attachStore( store )

  try { await store.dispatch( Auth.verify() ) } catch ( err ) { console.error( err ) }

  let theme
  if ( process.env.NODE_ENV === 'development' || process.env.USE_ACTIVE_THEME === 'true' ) {
    theme = new ActiveTheme( { altTheme : AppTheme, appearanceProjectId : process.env.UI_BUILDER_PROJECT_ID } )
  } else {
    theme = AppTheme
  }

  I18N.addIconDelegate( { 
    getIcon( key ) { 
      if ( theme.resources ) {
        const r = theme.resources( 'icon', key )
        if ( r == null && key.startsWith( 'icon.' ) ) {
          // If the i18n resource name starts with 'icon.' and we
          // didnt find it, do one more check against our resources without
          // the 'icon.' This allow resources in ui-builder to be specified
          // as 'delete', and not 'icon.delete'
          return theme.resources( 'icon', key.substring( 6 ) )
        }
        return r
      }
      return undefined
    }
  } )

  const mountNode = document.getElementById( 'root' )
  const root = ReactDOMClient.createRoot( mountNode )

  const preloadRoot = document.getElementById( 'preload-root' )
  preloadRoot?.parentElement?.removeChild( preloadRoot )

  root.render(
    <DndProvider backend={HTML5Backend}>

      <ApiProvider api={api}>
        <Provider store={store} api={api}>
          <Theme theme={theme}>
            <ErrorBoundary>
              <Dialogs />
              <ConnectedRouter history={history}>
                <App api={api} /> 
              </ConnectedRouter>
            </ErrorBoundary>
          </Theme>
        </Provider>
      </ApiProvider>
    </DndProvider>
  )
}

window.Main = { start }

document.addEventListener( 'DOMContentLoaded', start )
